// ---------------------------------------------------------------------------
// NATIONWIDE STATE CATALOG — single source of truth for the state landing
// pages (/en/states/:code, /es/estados/:code), the /states index, the footer's
// "Browse by Location" block and the sitemap.
//
// English names come from STATE_NAMES (src/lib/listingSettings.ts) and codes
// from US_STATES (src/data/constants.ts) — they are never retyped here. Only
// the Spanish exonyms, the Census region and a handful of verified licensing
// notes live in this file.
// ---------------------------------------------------------------------------
import { US_STATES } from '@/data/constants';
import { STATE_NAMES } from '@/lib/listingSettings';
import type { Lang } from '@/data/constants';

export type Region = 'west' | 'south' | 'midwest' | 'northeast';

export const REGIONS: { key: Region; en: string; es: string }[] = [
  { key: 'west', en: 'West', es: 'Oeste' },
  { key: 'south', en: 'South', es: 'Sur' },
  { key: 'midwest', en: 'Midwest', es: 'Medio Oeste' },
  { key: 'northeast', en: 'Northeast', es: 'Noreste' },
];

/** U.S. Census Bureau regions. */
const REGION_OF: Record<string, Region> = {
  AK: 'west', AZ: 'west', CA: 'west', CO: 'west', HI: 'west', ID: 'west', MT: 'west', NV: 'west',
  NM: 'west', OR: 'west', UT: 'west', WA: 'west', WY: 'west',
  AL: 'south', AR: 'south', DE: 'south', DC: 'south', FL: 'south', GA: 'south', KY: 'south',
  LA: 'south', MD: 'south', MS: 'south', NC: 'south', OK: 'south', SC: 'south', TN: 'south',
  TX: 'south', VA: 'south', WV: 'south',
  IL: 'midwest', IN: 'midwest', IA: 'midwest', KS: 'midwest', MI: 'midwest', MN: 'midwest',
  MO: 'midwest', NE: 'midwest', ND: 'midwest', OH: 'midwest', SD: 'midwest', WI: 'midwest',
  CT: 'northeast', ME: 'northeast', MA: 'northeast', NH: 'northeast', NJ: 'northeast',
  NY: 'northeast', PA: 'northeast', RI: 'northeast', VT: 'northeast',
};

/** Spanish exonyms. States not listed use the English name. */
const ES_NAME: Record<string, string> = {
  HI: 'Hawái', LA: 'Luisiana', MS: 'Misisipi', MO: 'Misuri', NJ: 'Nueva Jersey',
  NM: 'Nuevo México', NY: 'Nueva York', NC: 'Carolina del Norte', SC: 'Carolina del Sur',
  ND: 'Dakota del Norte', SD: 'Dakota del Sur', OR: 'Oregón', PA: 'Pensilvania',
  WV: 'Virginia Occidental', DC: 'Washington, D.C.', MI: 'Míchigan', KS: 'Kansas',
};

/**
 * Verified state-level licensing notes for the highest-volume markets. Every
 * other state gets the generic, accurate "check your state's board" answer.
 */
const LICENSE_NOTE: Record<string, { en: string; es: string }> = {
  CA: {
    en: 'California requires a CSLB (Contractors State License Board) license for construction work valued at $500 or more, including labor and materials.',
    es: 'California exige una licencia del CSLB (Contractors State License Board) para trabajos de construcción de $500 o más, incluyendo mano de obra y materiales.',
  },
  TX: {
    en: 'Texas has no statewide general contractor license, but electricians and HVAC contractors are licensed by TDLR and plumbers by the Texas State Board of Plumbing Examiners. Many cities add their own registration and permit rules.',
    es: 'Texas no tiene una licencia estatal de contratista general, pero los electricistas y contratistas de HVAC se licencian ante TDLR y los plomeros ante la Junta Estatal de Examinadores de Plomería. Muchas ciudades agregan su propio registro y permisos.',
  },
  FL: {
    en: 'Florida licenses contractors through the Construction Industry Licensing Board (CILB) under the Department of Business and Professional Regulation (DBPR). Certified licenses are valid statewide; registered licenses are limited to the local jurisdiction.',
    es: 'Florida licencia a los contratistas a través de la Junta de Licencias de la Industria de la Construcción (CILB) del DBPR. Las licencias certificadas valen en todo el estado; las registradas solo en la jurisdicción local.',
  },
  AZ: {
    en: 'Arizona requires most contractors to hold a license from the Arizona Registrar of Contractors (ROC), with separate residential, commercial and dual classifications.',
    es: 'Arizona exige que la mayoría de los contratistas tengan licencia del Arizona Registrar of Contractors (ROC), con clasificaciones residenciales, comerciales y duales.',
  },
  NV: {
    en: 'Nevada requires a license from the Nevada State Contractors Board (NSCB) for construction work, with a monetary limit set on each license.',
    es: 'Nevada exige una licencia de la Nevada State Contractors Board (NSCB) para trabajos de construcción, con un límite monetario asignado a cada licencia.',
  },
};

export interface UsState {
  /** Upper-case postal code, e.g. "TX" — matches mkt_listings.state. */
  code: string;
  /** Lower-case URL segment, e.g. "tx". */
  slug: string;
  en: string;
  es: string;
  region: Region;
  /** Dedicated deeper hub (counties/cities), if one exists. */
  hub?: string;
}

export const STATES: UsState[] = US_STATES.map((code) => ({
  code,
  slug: code.toLowerCase(),
  en: STATE_NAMES[code] || code,
  es: ES_NAME[code] || STATE_NAMES[code] || code,
  region: REGION_OF[code] || 'south',
  hub: code === 'CA' ? '/california' : undefined,
})).sort((a, b) => a.en.localeCompare(b.en));

export const stateBySlug = (slug?: string | null): UsState | undefined => {
  const s = (slug || '').trim().toLowerCase();
  return STATES.find((x) => x.slug === s);
};

export const stateLabel = (st: UsState, lang: Lang) => (lang === 'es' ? st.es : st.en);

/** State page path (pass through path() to localize: /states/tx -> /es/estados/tx). */
export const statePath = (st: UsState | string) =>
  `/states/${typeof st === 'string' ? st.toLowerCase() : st.slug}`;

export const statesInRegion = (region: Region) => STATES.filter((s) => s.region === region);

// ---------------------------------------------------------------------------
// STATE + CITY LANDING PAGES — /en/states/:code/:city  |  /es/estados/:code/:city
//
// Cities are NOT a hand-typed catalog: they come from the browse-listings
// `cities` / `cityIndex` aggregates (open-project counts per city, names +
// counts only). A city gets an indexable landing page while it is in the top
// CITY_PAGES_PER_STATE of its state. California is excluded because it has
// its own county/city hierarchy under /california.
//
// The slug rule below is mirrored EXACTLY in the `sitemap` edge function.
// ---------------------------------------------------------------------------
export const CITY_PAGES_PER_STATE = 10;

/** "Grand Prairie" -> "grand-prairie"; "Coeur d'Alene" -> "coeur-d-alene". */
export const citySlug = (city: string) =>
  city
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);

/** Best-effort display name from a slug (used only before the aggregate loads). */
export const cityNameFromSlug = (slug: string) =>
  slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** City pages exist for every state except California (which has /california/:city). */
export const hasCityPages = (st: UsState) => st.code !== 'CA';

/** Pass through path() to localize: /states/tx/austin -> /es/estados/tx/austin. */
export const stateCityPath = (st: UsState, city: string) => `${statePath(st)}/${citySlug(city)}`;

/**
 * Per-trade CITY money pages (/leads/flooring-leads-orlando-fl) exist for the
 * top CITY_MONEY_PAGES_PER_STATE cities of every non-California state — i.e.
 * the first N of the same ranked `cityIndex` list. Mirrored in the sitemap.
 */
export const CITY_MONEY_PAGES_PER_STATE = 5;



export const licenseNote = (st: UsState, lang: Lang): string => {
  const known = LICENSE_NOTE[st.code];
  if (known) return known[lang];
  return lang === 'es'
    ? `Los requisitos de licencia en ${st.es} los fija el estado y, en algunos oficios, la ciudad o el condado. Confirme con la junta de licencias de contratistas de ${st.es} antes de cotizar; los contratistas generales verifican licencia y seguro antes de adjudicar trabajo.`
    : `Licensing in ${st.en} is set by the state and, for some trades, by the city or county. Confirm requirements with ${st.en}'s contractor licensing board before bidding; general contractors verify license and insurance before awarding work.`;
};
